import React, { FC, useEffect, useState, useRef, useMemo } from "react";
import { renderToStaticMarkup as renderToString } from "react-dom/server";
import { getListingItem, getListingMapMetaData } from "@/services/api";
import GetListingMapMetaData from "@/components/landing-new/PropertySearch/components/listingMapMeta";
import Screen from "@/constants/screens";
import { useWindowSize } from "@/shared/hooks/useWindowSize";
import { Loader, X } from "lucide-react";
import PropertyCard from "@/atoms/PropertyCard";

type AreaMapProps = {
  postcodeCoordinates?: { lat: number; lng: number }[] | [];
  listingsLatLong: { id: number; location_lat: number; location_long: number }[];
  isMapDataLoading?: boolean;
};

const AreaMap: FC<AreaMapProps> = ({
  postcodeCoordinates,
  listingsLatLong,
  isMapDataLoading,
}): JSX.Element => {
  const [isGoogleMapsApiLoaded, setIsGoogleMapsApiLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const { width } = useWindowSize();
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const getListingMetaData = async (ids: number[]) => {
    try {
      const response = await getListingMapMetaData(ids);
      return response.result;
    } catch (error) {
      console.error("Error fetching listing meta data:", error);
    }
  };

  const getListingData = async (id: number) => {
    try {
      const data = await getListingItem(id.toString());
      return data;
    } catch (error) {
      console.error("Error fetching listing data:", error);
    }
  };

  const { center, groups } = useMemo(() => {
    let centreLat = 0;
    let centreLng = 0;
    let count = 0;
    const groupByLocation: { [key: string]: { lat: number; lng: number; ids: number[] } } = {};

    for (const listing of listingsLatLong) {
      const lat = Number(listing.location_lat);
      const lng = Number(listing.location_long);
      const key = `${lat},${lng}`;
      if (lat === 0) continue;
      if (!groupByLocation[key]) {
        count++;
        centreLat += lat;
        centreLng += lng;
        groupByLocation[key] = { lat, lng, ids: [] };
      }
      groupByLocation[key].ids.push(Number(listing.id));
    }

    return {
      center:
        count > 0
          ? { lat: centreLat / count, lng: centreLng / count }
          : { lat: 51.5074, lng: 0.1278 },
      groups: groupByLocation,
    };
  }, [listingsLatLong]);

  const initMap = (): void => {
    if (!mapRef.current || !window.google?.maps) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new google.maps.Map(mapRef.current, {
        zoom: 13,
        center: center,
        minZoom: 10,
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }

    const map = mapInstanceRef.current;

    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (postcodeCoordinates && postcodeCoordinates.length > 0) {
      polygonRef.current = new google.maps.Polygon({
        paths: postcodeCoordinates,
        strokeColor: "#FF0000",
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: "",
        fillOpacity: 0,
        map: map,
      });
    }

    const BASE_ICON_PATH =
      "M23.9957 0C10.3207 0 0 10.9296 0 25.4159C0 43.6493 22.31 60.0654 23.256 60.7577C23.4797 60.9221 23.7377 61 23.9957 61C24.2537 61 24.5117 60.9221 24.7354 60.7577C25.6814 60.0654 47.9914 43.6493 47.9914 25.4159C48 10.9296 37.6793 0 23.9957 0ZM23.9957 38.1282C16.3326 38.1282 10.1057 31.8543 10.1057 24.1525C10.1057 16.4507 16.3412 10.1768 23.9957 10.1768C31.6502 10.1768 37.8857 16.4507 37.8857 24.1525C37.8857 31.8543 31.6502 38.1282 23.9957 38.1282Z";
    const createMarkerIcon = (color: string) => ({
      path: BASE_ICON_PATH,
      fillColor: color,
      fillOpacity: 1,
      strokeWeight: 0,
      scale: 0.7,
      anchor: new google.maps.Point(27, 55),
    });

    const YELLOW_ICON = createMarkerIcon("#F39237");
    const PINK_ICON = createMarkerIcon("#BE1E64");

    infoWindowRef.current = new google.maps.InfoWindow();
    const infoWindow = infoWindowRef.current;

    let activeMarker: google.maps.Marker | null = null;
    const newBoundary = new google.maps.LatLngBounds();

    let zoomBeforeClick: number | null = null;
    let latBeforeClick: number | null = null;
    let lngBeforeClick: number | null = null;

    map.addListener("click", () => {
      infoWindow.close();
      if (zoomBeforeClick) {
        map.setZoom(zoomBeforeClick);
        map.setCenter(new google.maps.LatLng(latBeforeClick as number, lngBeforeClick as number));
        zoomBeforeClick = null;
        latBeforeClick = null;
        lngBeforeClick = null;
      }
      if (activeMarker) {
        activeMarker.setIcon(YELLOW_ICON);
        activeMarker = null;
      }
    });

    Object.values(groups).forEach((group) => {
      const marker = new google.maps.Marker({
        position: { lat: group.lat, lng: group.lng },
        map,
        icon: YELLOW_ICON,
      });
      newBoundary.extend(new google.maps.LatLng(group.lat, group.lng));

      markersRef.current.push(marker);
      marker.addListener("mouseover", () => {
        marker.setIcon(PINK_ICON);
      });
      marker.addListener("mouseout", () => {
        if (activeMarker !== marker) {
          marker.setIcon(YELLOW_ICON);
        }
      });
      marker.addListener("click", async () => {
        latBeforeClick = map.getCenter()?.lat() || null;
        lngBeforeClick = map.getCenter()?.lng() || null;
        if (activeMarker && activeMarker !== marker) {
          activeMarker.setIcon(YELLOW_ICON);
        }
        marker.setIcon(PINK_ICON);
        activeMarker = marker;
        map.panTo(marker.getPosition() as google.maps.LatLng);
        const zoom = map.getZoom();
        if (zoom && zoom < 15) {
          zoomBeforeClick = zoom;
          map.setZoom(15);
        }
        const loadingDemo = renderToString(
          <div className="custom-infowindow  overflow-y-auto rounded-2xl w-[200px] md:w-[330px] justify-center items-center">
            <div className="flex justify-center w-full items-center h-[80px] ">
              <Loader />
            </div>
          </div>,
        );

        infoWindow.setContent(loadingDemo);
        infoWindow.open(map, marker);
        let data = null;
        if (group.ids.length === 1 && width > Screen.Tablet_768) {
          data = await getListingData(group.ids[0]);
        } else {
          data = await getListingMetaData(group.ids);
        }
        const html = renderToString(
          <div
            className={`custom-infowindow ${
              Array.isArray(data) ? "max-h-[350px] max-w-[330px]" : "max-h-[500px] max-w-[600px] "
            } overflow-auto overflow-x-hidden rounded-2xl  `}
          >
            {Array.isArray(data)
              ? data.map((dataItem) => (
                  <div className="hover:opacity-70 border-b-grey-300 border-b" key={dataItem.id}>
                    <GetListingMapMetaData key={dataItem.id} data={dataItem} />{" "}
                  </div>
                ))
              : data && <PropertyCard property={data} infoWindow={true} />}

            <div className="w-full flex absolute mt-1 top-0 right-0 cursor-pointer">
              <a
                id="close-custom-info"
                className="ml-auto mr-1 cursor-pointer p-1 rounded-full shadow-sm hover:bg-grey-100 transition"
              >
                <X />
              </a>
            </div>
          </div>,
        );

        infoWindow.setContent(html);
        infoWindow.open(map, marker);

        google.maps.event.addListenerOnce(infoWindow, "domready", () => {
          const closeBtn = document.getElementById("close-custom-info");
          if (closeBtn) {
            closeBtn.addEventListener("click", () => {
              infoWindow.close();
              if (zoomBeforeClick) {
                map.setZoom(zoomBeforeClick);
                map.panTo(
                  new google.maps.LatLng(latBeforeClick as number, lngBeforeClick as number),
                );
                zoomBeforeClick = null;
                latBeforeClick = null;
                lngBeforeClick = null;
              }
              if (activeMarker) {
                activeMarker.setIcon(YELLOW_ICON);
                activeMarker = null;
              }
            });
          }

          const defaultCloseBtn = document.querySelector(".gm-ui-hover-effect");
          if (defaultCloseBtn) defaultCloseBtn.remove();

          const chrome = document.querySelector(".gm-style-iw-chr") as HTMLElement;
          if (chrome) chrome.style.display = "none";
        });
      });
    });
    if (postcodeCoordinates && postcodeCoordinates.length === 0) {
      if (Object.keys(groups).length === 1) {
        map.setCenter(newBoundary.getCenter());
        map.setZoom(15);
      } else {
        map.fitBounds(newBoundary);
      }
    }

    infoWindow.addListener("closeclick", () => {
      if (activeMarker) {
        activeMarker.setIcon(YELLOW_ICON);
        activeMarker = null;
      }
    });
  };

  const loadGoogleMapsApi = (): void => {
    if (window.google && window.google.maps) {
      setIsGoogleMapsApiLoaded(true);
      return;
    }

    const existingScript = document.getElementById("google-maps-api");
    if (existingScript) {
      existingScript.addEventListener("load", () => setIsGoogleMapsApiLoaded(true));
      return;
    }

    const apiKey =
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "AIzaSyCZ3c0k0tGh2mErBmcm_TyXrQAEEiD5Gjw";
    const script = document.createElement("script");
    script.id = "google-maps-api";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&language=en&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsGoogleMapsApiLoaded(true);

    document.body.appendChild(script);
  };
  useEffect(() => {
    if (isMapDataLoading) return;
    if (!isGoogleMapsApiLoaded) {
      loadGoogleMapsApi();
    } else {
      initMap();
    }

    return () => {
      if (mapInstanceRef.current) {
        google.maps.event.clearInstanceListeners(mapInstanceRef.current);
      }

      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];

      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }

      if (infoWindowRef.current) {
        google.maps.event.clearInstanceListeners(infoWindowRef.current);
        infoWindowRef.current.close();
      }
      mapInstanceRef.current = null;
    };
  }, [isGoogleMapsApiLoaded, isMapDataLoading]);

  return (
    <div className=" max-w-[1440px] mx-auto px-0 lg:pl-6 lg:pr-5">
      {!isMapDataLoading ? (
        <div
          ref={mapRef}
          id="polygonCityMap"
          className=" aspect-[2/4] md:aspect-square lg:aspect-[4/2] my-5 max-w-[1440px]"
        ></div>
      ) : (
        <div className={`flex flex-col w-full mb-8 md:px-4`}>
          <Loader className="animate-spin mx-auto mt-20" />
        </div>
      )}
    </div>
  );
};

export default AreaMap;
